import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { RootState, store } from '../..';
import { Student } from '@/models/student/student';
import { StudentKartePrevNext } from '@/models/student/student';
import { studentService } from '@/service/api/student.service';
import { PagingData } from '@/models/core/paging-data';
import { SchoolGroup } from '@/models/school-group/school-group';
import { ImportStudentResults } from '@/models/student/import-students-result';
import { schoolClassService } from '@/service/api/school-class.service';
import FileSaver from 'file-saver';
import { StudentKarte } from '@/models/student/student-karte';
import { studentKarteService } from '@/service/api/student-karte.service';
import { StudentCompetencySummary } from '@/models/student/student-competency-summary';
import { studentCompetencySummaryService } from '@/service/api/student-competency-summary.service';
import { StudentCompetencyData } from '@/models/student/student-competency-data';
import { studentCompetencyDataService } from '@/service/api/student-competency-data.service';
import { Tag } from '@/models/tag';
import { StudentSearch } from '@/models/student/student-search';
import { StudentGoodPoints } from '@/models/student/good-points';
import { Big5EchartService } from '@/service/echart/big5.echart.service';
import { StudentsByClass } from '@/models/student/students-by-class';
import { UpdateCount, StudentSchoolClassMovement, UpdateStudentProfilesRequest } from '@/models/student/students-arrange';
import { StudentPromotionRequest } from '@/models/student/student-promotion-request';
import { DataList } from '@/models/core/data-list';
import { GetStudentRequest } from '@/models/student/get-student-request';
import { ExistStudentResponse } from '@/models/student/exist-student-response';
import { PostStudentFileRequest } from '@/models/student/post-student-file-request';
import { StudentFindingsProposals } from '@/models/student/student-findings-proposals';
import { StudentTrends } from '@/models/student/student-trends';
import { TrendEchartService } from '@/service/echart/trend.echart.service';
import { studentFilterModule } from '@/store/modules/frames/student-filters.module';
import { RecommendationLetter } from '@/models/student/recommendation-letter';
import { GenerateRecommendationLetterRequest } from '@/models/student/generate-recommendation-letter-request';
import { RecommendationLetterUpdateRequest } from '@/models/student/recommendation-letter-update-request';

export interface IStudentState {
  students: PagingData<Student>; // 生徒情報一覧
  student: Student; // 生徒情報
  lastYearStudent: Student; // 昨年度の生徒情報
  schoolGroups: SchoolGroup[]; // 生徒所属グループ
  importStudentResults: ImportStudentResults; // 生徒インポートレスポンスパラメータ
  studentKarte: StudentKarte; // 生徒カルテ情報
  studentCompetencySummary: StudentCompetencySummary; // 生徒サマリー情報
  studentCompetencyData: StudentCompetencyData; // 生徒コンピテンシー情報
  studentFindingsProposals: StudentFindingsProposals; // 生徒の所見提案情報
  studentTrends: StudentTrends; // 生徒の傾向チェック結果情報
  studentGoodPoints: StudentGoodPoints; // 強み評価一覧
  big5EchartService: Big5EchartService; // big5チャート
  trendEchartService: TrendEchartService; // trendチャート
  studentsBySchoolClasses: StudentsByClass[]; //クラス別生徒情報(D&D編集ための変数)
  studentSearchParams?: StudentSearch; // 生徒の検索条件
  recommendationLetters: RecommendationLetter[]; // 推薦書一覧
  currentRecommendationLetter: RecommendationLetter | null; // 現在の推薦書
}
@Module({ dynamic: true, store, name: 'studentModule', namespaced: true })
class StudentModule extends VuexModule implements IStudentState {
  students: PagingData<Student> = new PagingData<Student>();
  student: Student = new Student();
  lastYearStudent: Student = new Student();
  schoolGroups: SchoolGroup[] = [];
  importStudentResults: ImportStudentResults = new ImportStudentResults();
  studentKarte: StudentKarte = new StudentKarte();
  studentCompetencySummary: StudentCompetencySummary = new StudentCompetencySummary();
  studentCompetencyData: StudentCompetencyData = new StudentCompetencyData();
  studentFindingsProposals: StudentFindingsProposals = new StudentFindingsProposals();
  studentTrends: StudentTrends = new StudentTrends();
  studentGoodPoints: StudentGoodPoints = new StudentGoodPoints();
  big5EchartService: Big5EchartService = new Big5EchartService();
  trendEchartService: TrendEchartService = new TrendEchartService();
  studentsBySchoolClasses: StudentsByClass[] = [];
  studentSearchParams?: StudentSearch = {};
  studentKartePrevNext: StudentKartePrevNext = new StudentKartePrevNext();
  studentKarteReferrer: string = '';
  recommendationLetters: RecommendationLetter[] = [];
  currentRecommendationLetter: RecommendationLetter | null = null;

  // クラス別生徒人数
  get studentCountBySchoolClassID(): Map<number, number> {
    if (this.students === undefined || this.students.data === undefined || this.students.data.length === 0) {
      return new Map<number, number>();
    }

    return this.students.data.reduce((result: Map<number, number>, student: Student) => {
      if (!result.has(student.schoolClass.schoolClassID)) {
        result.set(student.schoolClass.schoolClassID, 0);
      }
      let count: number = result.get(student.schoolClass.schoolClassID);
      count += 1;
      result.set(student.schoolClass.schoolClassID, count);

      return result;
    }, new Map<number, number>());
  }

  // 生徒ID -> 生徒情報
  get studentByIDMap(): Map<number, Student> {
    if (this.students && this.students.data) {
      return this.students.data.reduce((result: Map<number, Student>, student: Student) => {
        result.set(student.studentID, student);
        return result;
      }, new Map<number, Student>());
    }
    return new Map<number, Student>();
  }

  // クラス編成のD&D結果の中で編集した生徒の前後の情報
  get studentSchoolClassMovements(): StudentSchoolClassMovement[] {
    return this.studentsBySchoolClasses.reduce((result: StudentSchoolClassMovement[], studentsBySchoolClass: StudentsByClass) => {
      const moveStudents: StudentSchoolClassMovement[] = studentsBySchoolClass.students.reduce((ret: StudentSchoolClassMovement[], student: Student) => {
        if (studentsBySchoolClass.schoolClass.schoolClassID !== student.schoolClass.schoolClassID) {
          // クラスが変更されていたら
          ret.push(
            new StudentSchoolClassMovement().deserialize({
              student: student,
              oldSchoolClass: student.schoolClass,
              newSchoolClass: studentsBySchoolClass.schoolClass,
            })
          );
        }
        return ret;
      }, []);
      result = result.concat(moveStudents);
      return result;
    }, []);
  }

  get kartePrevNext(): StudentKartePrevNext {
    return this.studentKartePrevNext;
  }

  // 直近の RecommendationLetter を取得
  get latestRecommendationLetter(): RecommendationLetter {
    return this.recommendationLetters.sort((a: RecommendationLetter, b: RecommendationLetter) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }

  @Mutation
  setStudents(value: PagingData<Student>) {
    this.students = value;
  }

  @Mutation
  setStudent(value: Student) {
    this.student = value;
  }

  @Mutation
  setLastYearStudent(value: Student) {
    this.lastYearStudent = value;
  }

  @Mutation
  setSchoolGroups(value: SchoolGroup[]) {
    this.schoolGroups = value;
  }

  @Mutation
  setImportStudentResults(value: ImportStudentResults) {
    this.importStudentResults = value;
  }

  @Mutation
  setStudentKarte(value: StudentKarte) {
    this.studentKarte = value;
  }

  @Mutation
  clearStudentFileResponse() {
    this.importStudentResults = undefined;
  }

  @Mutation
  clearStudents() {
    this.students = new PagingData<Student>();
  }

  @Mutation
  setStudentCompetencySummary(value: StudentCompetencySummary) {
    this.studentCompetencySummary = value;
  }

  @Mutation
  setStudentCompetencyData(value: StudentCompetencyData) {
    this.studentCompetencyData = value;
  }

  @Mutation
  setStudentFindingsProposals(value: StudentFindingsProposals) {
    this.studentFindingsProposals = value;
  }

  @Mutation
  setStudentTrends(value: StudentTrends) {
    this.studentTrends = value;
  }

  @Mutation
  addTag(value: Tag) {
    this.student.tag.push(value);
  }

  @Mutation
  setStudentGoodPoints(value: StudentGoodPoints) {
    this.studentGoodPoints = value;
  }

  @Mutation
  setStudentsByClasses(value: StudentsByClass[]) {
    this.studentsBySchoolClasses = value;
  }

  @Mutation
  setStudentsSearchParams(value: StudentSearch) {
    this.studentSearchParams = value;
  }

  @Mutation
  setStudentKarteReferrer(value: string) {
    this.studentKarteReferrer = value;
  }

  @Mutation
  setStudentKartePrevNext(value: StudentKartePrevNext) {
    this.studentKartePrevNext = value;
  }

  @Mutation
  setRecommendationLetters(value: RecommendationLetter[]) {
    this.recommendationLetters = value;
  }

  @Mutation
  setCurrentRecommendationLetter(value: RecommendationLetter | null) {
    this.currentRecommendationLetter = value;
  }

  @Mutation
  clean() {
    this.student = new Student();
    this.schoolGroups = [];
    this.studentKarte = new StudentKarte();
    this.studentCompetencySummary = new StudentCompetencySummary();
    this.studentCompetencyData = new StudentCompetencyData();
    this.studentFindingsProposals = new StudentFindingsProposals();
    this.studentTrends = new StudentTrends();
    this.studentGoodPoints = new StudentGoodPoints();
    this.big5EchartService = new Big5EchartService();
    this.trendEchartService = new TrendEchartService();
    this.studentsBySchoolClasses = [];
    this.recommendationLetters = [];
    this.currentRecommendationLetter = null;
  }

  /**
   * 生徒情報一覧取得
   * @param studentSearch 生徒情報一覧取得リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchStudents(studentSearch: StudentSearch) {
    // 検索条件の保持
    this.setStudentsSearchParams(studentSearch);
    const result = await studentService.getStudents(
      studentSearch.targetType,
      studentSearch.targetID,
      studentSearch.limit,
      studentSearch.offset,
      studentSearch.sortKey,
      studentSearch.sortType,
      studentSearch.keyword || '',
      studentSearch.selectedTagIDs,
      studentSearch.hasScore,
      studentSearch.studentNumber
    );
    this.setStudents(result);
  }
  /**
   * 生徒情報取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async getStudent(getStudentRequest: GetStudentRequest) {
    const result = await studentService.getStudent(getStudentRequest.studentID, getStudentRequest.headers);
    if (getStudentRequest.isLastYear) {
      this.setLastYearStudent(result);
    } else {
      this.setStudent(result);
    }
  }

  /**
   * ある学生が所属しているグループを取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchSchoolGroups(studentID: number) {
    const result = await schoolClassService.getSchoolGroups(studentID);
    this.setSchoolGroups(result);
  }

  /**
   * 生徒インポート前の差分確認
   * @param postStudentFileRequest 生徒インポート用リクエスト
   */
  @Action({ rawError: true })
  async checkStudentDifferences(postStudentFileRequest: PostStudentFileRequest) {
    return await studentService.checkStudentDifferences(postStudentFileRequest.formData, postStudentFileRequest.canChangePassword);
  }

  /**
   * 生徒インポート
   * @param postStudentFileRequest 生徒インポート用リクエスト
   */
  @Action({ rawError: true })
  async postStudentFile(postStudentFileRequest: PostStudentFileRequest) {
    return await studentService.sendStudentExcelFile(postStudentFileRequest.formData, postStudentFileRequest.canChangePassword);
  }

  /**
   * 受検設定更新
   * @param updateEvalSettingDetail 受検設定更新情報
   */
  @Action({ rawError: true })
  async downloadRegistrationTemplate() {
    const file = await studentService.downloadStudentRegistrationTemplate();
    FileSaver.saveAs(file, '登録者一括登録用テンプレート.xlsx');
  }

  /**
   * 生徒カルテ取得
   * @param studentKarteParams 受検設定更新情報
   */
  @Action({ rawError: true })
  async fetchStudentKarte(studentID: number) {
    const result = await studentKarteService.getStudentKarte(studentID);
    this.setStudentKarte(result);
    this.big5EchartService.setBig5Scores(result.big5Scores);
  }

  /**
   * ある学生のコンピテンシーサマリーを取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchStudentCompetencySummary(studentID: number) {
    const result = await studentCompetencySummaryService.getStudentCompetencySummary(studentID);
    this.setStudentCompetencySummary(result);
  }

  /**
   * ある学生のコンピテンシー情報を取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchStudentCompetencyData(studentID: number) {
    const result = await studentCompetencyDataService.getStudentCompetencyData(studentID);
    this.setStudentCompetencyData(result);
  }

  /**
   * ある学生の所見提案を取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchStudentFindingsProposals(studentID: number) {
    const result = await studentKarteService.getStudentFindingsProposals(studentID);
    this.setStudentFindingsProposals(result);
  }

  /**
   * ある学生の傾向チェック結果を取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchStudentTrends(studentID: number) {
    const result = await studentKarteService.getStudentTrends(studentID);
    this.setStudentTrends(result);
    this.trendEchartService.setTrendScores(result.latestResults);
  }

  /**
   * 学生情報更新
   */
  @Action({ rawError: true })
  async updateStudent(priorityTagName: string) {
    const params = {
      firstName: this.student.firstName,
      firstNameKana: this.student.firstNameKana,
      lastName: this.student.lastName,
      lastNameKana: this.student.lastNameKana,
      schoolClassID: this.student.schoolClass.schoolClassID,
      tag: this.student.selectedTagContents,
      priorityTagName: priorityTagName,
      email: this.student.email,
      manageNumber: this.student.manageNumber,
    };
    const result = await studentService.updateStudent(params, this.student.studentID);
    this.setStudent(result);
  }

  /**
   * パスワード更新
   * @param password パスワード
   */
  @Action({ rawError: true })
  async updatePassword(password: string) {
    const params = {
      password: password,
    };
    await studentService.updatePassword(params, this.student.studentID);
  }

  /**
   * 生徒削除
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async deleteStudent(studentID: number) {
    await studentService.deleteStudent(studentID);
  }

  /**
   * 強み評価取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchGoodPoints(studentID: number) {
    const result = await studentService.fetchGoodPoints(studentID);
    this.setStudentGoodPoints(result);
  }

  /**
   * 生徒プロフィール一括更新(一括クラス変更)
   * @param UpdateStudentProfilesRequest リクエストボディ(生徒のクラス変更or退学・転校情報)
   */
  @Action({ rawError: true })
  async updateStudentProfiles(updateStudentProfilesRequest: DataList<UpdateStudentProfilesRequest>): Promise<UpdateCount> {
    const result = await studentService.updateStudentProfiles(updateStudentProfilesRequest);
    return result;
  }

  /**
   * 生徒進級
   * @param StudentPromotionRequest リクエストボディ(進級処理で使用するクラスの前後のセット情報)
   */
  @Action({ rawError: true })
  async promoteStudents(promoteStudents: DataList<StudentPromotionRequest>) {
    await studentService.promoteStudents(promoteStudents);
  }

  /**
   * 生徒存在チェック
   * @param studentID
   */
  @Action({ rawError: true })
  async existStudent(getStudentRequest: GetStudentRequest): Promise<ExistStudentResponse> {
    return studentService.existStudent(getStudentRequest.studentID, getStudentRequest.headers);
  }

  /**
   * 所見提案リストダウンロード
   * @param studentSearch 生徒情報一覧取得リクエストパラメータ
   */
  @Action({ rawError: true })
  downloadFindingsProposalsList(studentSearch: StudentSearch) {
    return studentService.downloadFindingsProposalsList(studentSearch.targetType, studentSearch.targetID, studentSearch.keyword || '', studentSearch.selectedTagIDs);
  }

  /**
   * 生徒情報一覧取得
   * @param studentSearch 生徒情報一覧取得リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchStudentKartePrevNext(studentID: number) {
    const result = await studentService.fetchStudentKartePrevNext(
      studentID,
      studentFilterModule.targetType,
      studentFilterModule.targetID,
      this.studentSearchParams.sortKey,
      this.studentSearchParams.sortType,
      studentFilterModule.searchWord || '',
      studentFilterModule.commaSelectedTagIDs,
      true,
      studentFilterModule.studentNumber
    );
    this.setStudentKartePrevNext(result);
  }

  /**
   * 生徒の推薦書一覧を取得
   * @param studentID 生徒ID
   */
  @Action({ rawError: true })
  async fetchStudentRecommendationLetters(studentID: number) {
    const result = await studentKarteService.getStudentRecommendationLetters(studentID);
    this.setRecommendationLetters(result);
  }

  /**
   * 特定の推薦書を取得
   * @param params 生徒IDと推薦書ID
   */
  @Action({ rawError: true })
  async fetchRecommendationLetter(params: { studentID: number; id: number }) {
    const result = await studentKarteService.getRecommendationLetter(params.studentID, params.id);
    this.setCurrentRecommendationLetter(result);
  }

  /**
   * 推薦書を削除
   * @param params 生徒IDと推薦書ID
   */
  @Action({ rawError: true })
  async deleteRecommendationLetter(params: { studentID: number; id: number }) {
    await studentKarteService.deleteRecommendationLetter(params.studentID, params.id);
  }

  /**
   * 推薦書を生成する
   * @param generateRecommendationLetterRequest 推薦書生成リクエスト
   */
  @Action({ rawError: true })
  async generateRecommendationLetter(generateRecommendationLetterRequest: GenerateRecommendationLetterRequest) {
    const result = await studentKarteService.generateRecommendationLetter(generateRecommendationLetterRequest);
    return result;
  }

  /**
   * 推薦書のポリシーを生成する
   * @param generateRecommendationLetterPoliciesRequest 推薦書ポリシー生成リクエスト
   */
  @Action({ rawError: true })
  async generateRecommendationLetterPolicies(generateRecommendationLetterPoliciesRequest: GenerateRecommendationLetterRequest) {
    const result = await studentKarteService.generateRecommendationLetterPolicies(generateRecommendationLetterPoliciesRequest);
    return result;
  }

  @Action
  async updateRecommendationLetter(data: RecommendationLetterUpdateRequest) {
    return studentKarteService.updateRecommendationLetter(data);
  }
}

export const studentModule = getModule(StudentModule);
